import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { userService, UserProfile } from '../services/userService';
import { Event } from '../services/eventService';
import { EventCard } from '../components/EventCard';
import { LoadingSpinner } from '../components/ui';
import { ReportModal } from '../components/moderation/ReportModal';
import { trackUserFollowed, trackProfileViewed } from '../services/analyticsService';

export function UserProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [createdEvents, setCreatedEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    if (id) {
      loadUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (currentUser && id && currentUser.uid !== id) {
      // Set up real-time follow status listener
      const unsubscribeFollow = userService.subscribeToFollowStatus(
        currentUser.uid,
        id,
        setIsFollowing
      );

      // Set up real-time profile updates listener
      const unsubscribeProfile = userService.subscribeToUserProfile(
        id,
        (profile) => {
          if (profile) {
            setUserProfile(profile);
          }
        }
      );

      return () => {
        unsubscribeFollow();
        unsubscribeProfile();
      };
    }
  }, [currentUser, id]);

  const loadUserProfile = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      
      // Load user profile
      const profile = await userService.getUserProfile(id);
      if (!profile) {
        setError('User not found');
        return;
      }
      
      setUserProfile(profile);

      // Track profile view
      trackProfileViewed({
        profile_user_id: id,
        is_own_profile: currentUser?.uid === id,
      });

      // Load user's created events (favorites are private)
      const created = await userService.getUserCreatedEvents(id);
      setCreatedEvents(created);

      // Check initial follow status if current user is logged in
      if (currentUser && currentUser.uid !== id) {
        const following = await userService.isFollowing(currentUser.uid, id);
        setIsFollowing(following);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!currentUser || !id || followLoading) return;

    try {
      setFollowLoading(true);

      if (isFollowing) {
        await userService.unfollowUser(currentUser.uid, id);
        trackUserFollowed({ target_user_id: id, is_following: false });
      } else {
        await userService.followUser(currentUser.uid, id);
        trackUserFollowed({ target_user_id: id, is_following: true });
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);
      // Show error to user - you might want to add a toast notification here
      alert(error instanceof Error ? error.message : 'Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 text-lg font-medium mb-2">{error}</div>
        <p className="text-gray-600">The user you're looking for doesn't exist or has been removed.</p>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">User profile not found.</p>
      </div>
    );
  }

  const isOwnProfile = currentUser?.uid === id;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          {/* Profile Picture */}
          <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 relative flex-shrink-0">
            {userProfile.photoURL ? (
              <img
                src={userProfile.photoURL}
                alt={userProfile.displayName}
                className="absolute inset-0 w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                <span className="text-3xl font-bold text-white">
                  {userProfile.displayName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
              <h1 className="text-3xl font-bold text-gray-900">
                {userProfile.displayName}
              </h1>
              
              {!isOwnProfile && currentUser && (
                <div className="flex gap-2">
                  <button
                    onClick={handleFollowToggle}
                    disabled={followLoading}
                    className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                      isFollowing
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-primary-500 text-white hover:bg-primary-600'
                    } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {followLoading ? (
                      <div className="flex items-center gap-2">
                        <LoadingSpinner size="sm" />
                        {isFollowing ? 'Unfollowing...' : 'Following...'}
                      </div>
                    ) : (
                      isFollowing ? 'Following' : 'Follow'
                    )}
                  </button>
                  <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                    Message
                  </button>
                  <button
                    onClick={() => setShowReportModal(true)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Report this user"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            
            {/* Stats */}
            <div className="flex justify-center md:justify-start gap-8 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {userProfile.eventsCreatedCount || 0}
                </div>
                <div className="text-sm text-gray-600">Events</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {userProfile.followersCount || 0}
                </div>
                <div className="text-sm text-gray-600">Followers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {userProfile.followingCount || 0}
                </div>
                <div className="text-sm text-gray-600">Following</div>
              </div>
            </div>
            
            {/* Bio */}
            {userProfile.bio && (
              <div className="mb-4">
                <p className="text-gray-700">{userProfile.bio}</p>
              </div>
            )}

            {/* Social Links */}
            {userProfile.socialLinks && Object.keys(userProfile.socialLinks).length > 0 && (
              <div className="flex gap-3">
                {userProfile.socialLinks.instagram && (
                  <a
                    href={`https://instagram.com/${userProfile.socialLinks.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-600 hover:text-pink-700 transition-colors"
                    title={`@${userProfile.socialLinks.instagram} on Instagram`}
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                )}
                {userProfile.socialLinks.tiktok && (
                  <a
                    href={`https://tiktok.com/@${userProfile.socialLinks.tiktok}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black hover:text-gray-700 transition-colors"
                    title={`@${userProfile.socialLinks.tiktok} on TikTok`}
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                )}
                {userProfile.socialLinks.x && (
                  <a
                    href={`https://x.com/${userProfile.socialLinks.x}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-black hover:text-gray-700 transition-colors"
                    title={`@${userProfile.socialLinks.x} on X`}
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                )}
                {userProfile.socialLinks.youtube && (
                  <a
                    href={`https://youtube.com/@${userProfile.socialLinks.youtube}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700 transition-colors"
                    title={`@${userProfile.socialLinks.youtube} on YouTube`}
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                )}
              </div>
            )}
            
            {/* Join Date */}
            <p className="text-sm text-gray-500">
              Joined {userProfile.createdAt?.toDate().toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Events Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900">
              Events Created ({createdEvents.length})
            </h2>
          </div>
        </div>

        <div className="p-6">
          {createdEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {createdEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {isOwnProfile ? "You haven't created any events yet" : `${userProfile.displayName} hasn't created any events yet`}
              </h3>
              <p className="text-gray-600">
                {isOwnProfile ? "Start creating events to build your community!" : "Check back later for new events."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Report Modal */}
      {userProfile && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          targetType="user"
          targetId={userProfile.uid}
          targetTitle={userProfile.displayName}
        />
      )}
    </div>
  );
}